// @ts-nocheck — wie PlateMarkdownEditor: TypeScript 4.9 läuft an den Plate-Typen in einen Stapelüberlauf.
/**
 * Gemeinsames Bearbeiten im Editor: wer gerade im Abschnitt ist, und wo die anderen schreiben.
 *
 * Die Daten kommen aus der Awareness von Yjs (Hocuspocus): je Teilnehmer Name, Farbe und die
 * Auswahl als relative Yjs-Position. Die Position wird in eine Slate-Auswahl und dann in Rechtecke
 * im Editor umgerechnet und als Balken bzw. Schatten über den Text gelegt.
 */
import { useEffect, useState } from "react";
import { useEditorRef, usePluginOption } from "platejs/react";
import { YjsPlugin } from "@platejs/yjs/react";
import { CursorEditor, relativeRangeToSlateRange } from "@slate-yjs/core";

/** Feste Farbe je Person, aus der E-Mail-Adresse. */
export const colorFor = (key: string) => {
  let h = 0;
  for (const ch of key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return `hsl(${h % 360} 70% 45%)`;
};

const others = (awareness) =>
  Array.from(awareness?.getStates?.() ?? new Map())
    .filter(([id, state]) => id !== awareness.clientID && state?.data?.name)
    .map(([id, state]) => ({ id, ...state.data }));

/** Namen der anderen im Abschnitt, mit Verbindungsstand. */
export function PresenceBar({ labels }) {
  const editor = useEditorRef();
  const awareness = usePluginOption(YjsPlugin, "awareness");
  const connected = usePluginOption(YjsPlugin, "_isConnected");
  const synced = usePluginOption(YjsPlugin, "_isSynced");
  const [people, setPeople] = useState([]);
  useEffect(() => {
    if (!awareness) return;
    const update = () => setPeople(others(awareness));
    update();
    awareness.on("change", update);
    return () => awareness.off("change", update);
  }, [awareness, editor]);
  return (
    <div className="flex flex-wrap items-center gap-2 border-b px-2 py-1 text-xs text-muted-foreground" data-testid="presence">
      <span className={`h-2 w-2 rounded-full ${connected && synced ? "bg-emerald-500" : "bg-amber-500"}`} />
      <span>{connected && synced ? labels.live : labels.connecting}</span>
      {people.length ? <span className="ml-2">{labels.alsoHere}</span> : null}
      {people.map((p) => (
        <span key={p.id} className="rounded-full px-2 py-0.5 text-white" style={{ background: p.color }} data-presence-name={p.name}>
          {p.name}
        </span>
      ))}
    </div>
  );
}

/** Cursor und Auswahl der anderen, über den Editor gelegt (innerhalb eines `relative` Rahmens). */
export function RemoteCursors({ container }) {
  const editor = useEditorRef();
  const awareness = usePluginOption(YjsPlugin, "awareness");
  const [marks, setMarks] = useState([]);
  useEffect(() => {
    if (!awareness || !container) return;
    let frame = 0;
    const compute = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const out = [];
        const box = container.getBoundingClientRect();
        let states = {};
        try {
          states = CursorEditor.cursorStates(editor);
        } catch {
          return setMarks([]);
        }
        for (const [clientId, state] of Object.entries(states)) {
          if (!state?.relativeSelection || !state?.data) continue;
          try {
            const range = relativeRangeToSlateRange(editor.sharedRoot, editor, state.relativeSelection);
            if (!range) continue;
            const dom = editor.api.toDOMRange(range);
            if (!dom) continue;
            const rects = Array.from(dom.getClientRects());
            const collapsed = range.anchor.offset === range.focus.offset && JSON.stringify(range.anchor.path) === JSON.stringify(range.focus.path);
            const last = rects[rects.length - 1] ?? dom.getBoundingClientRect();
            out.push({
              id: clientId,
              color: state.data.color,
              name: state.data.name,
              caret: { left: (collapsed ? last.left : last.right) - box.left + container.scrollLeft, top: last.top - box.top + container.scrollTop, height: last.height || 18 },
              rects: collapsed ? [] : rects.map((r) => ({ left: r.left - box.left + container.scrollLeft, top: r.top - box.top + container.scrollTop, width: r.width, height: r.height })),
            });
          } catch {
            // Position passt nicht mehr zum Inhalt (gerade geändert) — beim nächsten Durchlauf.
          }
        }
        // Nur bei Änderung neu zeichnen — das Intervall liefe sonst zweimal je Sekunde durch React.
        setMarks((prev) => (JSON.stringify(prev) === JSON.stringify(out) ? prev : out));
      });
    };
    compute();
    awareness.on("change", compute);
    CursorEditor.on?.(editor, "change", compute);
    const timer = setInterval(compute, 500);
    return () => {
      awareness.off("change", compute);
      CursorEditor.off?.(editor, "change", compute);
      clearInterval(timer);
      cancelAnimationFrame(frame);
    };
  }, [awareness, editor, container]);
  return (
    <div className="pointer-events-none absolute inset-0 z-10" aria-hidden>
      {marks.map((m) => (
        <div key={m.id} data-remote-cursor={m.name}>
          {m.rects.map((r, i) => (
            <div key={i} className="absolute" style={{ ...r, background: m.color, opacity: 0.18 }} />
          ))}
          <div className="absolute" style={{ left: m.caret.left, top: m.caret.top, height: m.caret.height, width: 2, background: m.color }}>
            <div className="absolute -top-4 left-0 whitespace-nowrap rounded px-1 text-[10px] leading-4 text-white" style={{ background: m.color }}>
              {m.name}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
